import { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Chip,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import moment from "moment";
import { formatCurrency } from "../../../../../utils/mockWalletData";

/**
 * CommissionPaymentHistory Tab - Shows completed commission payment transactions
 */

function getPaymentMethodIcon(method) {
  if (method === 'ONLINE') {
    return {
      icon: CreditCardIcon,
      label: 'Online',
      color: 'success',
      tooltip: 'Online Payment (Credit Card / Gateway)'
    };
  } else {
    return {
      icon: AccountBalanceIcon,
      label: 'Offline',
      color: 'primary',
      tooltip: 'Offline Payment (Bank Transfer / Check)'
    };
  }
}

function ExpandableRow({ transaction }) {
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <>
      <TableRow sx={{ "&:hover": { bgcolor: "grey.50" } }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="View Details">
              <IconButton
                aria-label="view details"
                size="small"
                onClick={() => setDetailsOpen(true)}
              >
                <InfoOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
        </TableCell>
        <TableCell sx={{ fontWeight: "bold" }}>{transaction.to?.name}</TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {formatCurrency(transaction.amount)}
        </TableCell>
        <TableCell>
          <Typography variant="caption">
            {new Date(transaction.date).toLocaleDateString()}
          </Typography>
        </TableCell>
        <TableCell>
          {(() => {
            const paymentInfo = getPaymentMethodIcon(transaction.paymentMethod);
            const IconComponent = paymentInfo.icon;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconComponent
                  sx={{ fontSize: '1.2rem', color: `${paymentInfo.color}.main` }}
                  titleAccess={paymentInfo.tooltip}
                />
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  {paymentInfo.label}
                </Typography>
              </Box>
            );
          })()}
        </TableCell>
        <TableCell>
          <Chip
            label={transaction.status}
            size="small"
            color={getStatusColor(transaction.status)}
          />
        </TableCell>
      </TableRow>

      {/* Expandable Details */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2 }}>
                Transaction Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Transaction ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {transaction.id}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Reference Number
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {transaction.referenceNumber || "N/A"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#064d51' }}>
          Commission Payment Transaction Details
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Partner Organization */}
          <Box sx={{ mb: 3, p: 1.5, bgcolor: '#f5f5f5', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#064d51' }}>
              Channel Partner Organization
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.3 }}>
                  FROM
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {transaction.from?.name || 'Thaklis'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {transaction.from?.type || 'Organization'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.3 }}>
                  TO (CHANNEL PARTNER)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {transaction.to?.name || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {transaction.to?.type || 'Channel Partner'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Bank Information */}
          <Box sx={{ mb: 3, p: 1.5, bgcolor: '#f5f5f5', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#064d51' }}>
              Bank Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.3 }}>
                  SOURCE BANK
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {transaction.sourceBank || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.3 }}>
                  DESTINATION BANK
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {transaction.destinationBank || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Transaction Details */}
          <Box sx={{ mb: 3, p: 1.5, bgcolor: '#f5f5f5', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#064d51' }}>
              Transaction Details
            </Typography>
            <Stack spacing={1.2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Transaction ID</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{transaction.id || 'N/A'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Reference Number</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{transaction.referenceNumber || 'N/A'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Payment Date</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {transaction.date ? moment(transaction.date).format("MMM DD, YYYY") : "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{transaction.paymentMethod || 'Bank Transfer'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Chip label={transaction.status} size="small" color={getStatusColor(transaction.status)} />
              </Box>
            </Stack>
          </Box>

          {/* Ticket Breakdown */}
          {transaction.tickets && transaction.tickets.length > 0 && (
            <Box sx={{ mb: 3, p: 1.5, bgcolor: '#f5f5f5', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#064d51' }}>
                Associated Tickets ({transaction.tickets.length})
              </Typography>
              <Stack spacing={1}>
                {transaction.tickets.map((ticket, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', pb: 1, borderBottom: idx < transaction.tickets.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                        {ticket.ticketNumber}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {ticket.serviceName}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#FF9800' }}>
                      {formatCurrency(ticket.commission)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Commission Amount */}
          <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#064d51' }}>
              Commission Amount
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Total Commission</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF9800' }}>
                {formatCurrency(transaction.amount)}
              </Typography>
            </Box>
            {transaction.notes && (
              <>
                <Divider sx={{ my: 1.2 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.4 }}>
                  NOTES
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {transaction.notes}
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailsOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#064d51' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const CommissionPaymentHistory = ({ data }) => {
  if (!data || data.items.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="body2">
          No commission payment transactions found.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "primary.main" }}>
            <TableRow>
              <TableCell width="40" sx={{ fontWeight: "bold", color: "white" }} />
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>To (Channel Partner)</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", color: "white" }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Date</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Payment Method</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((transaction) => (
              <ExpandableRow key={transaction.id} transaction={transaction} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CommissionPaymentHistory;
